/** Types shared by server and client. */

export const AllRoleTypes = ['admin', 'user'] as const;
export type RoleType = (typeof AllRoleTypes)[number];

/** where a payment attempt is: 'pending' while the user is at the payment provider, then one of the final states */
export const AllTransactionStatusTypes = ['pending', 'success', 'failed', 'canceled'] as const;
export type TransactionStatusType = (typeof AllTransactionStatusTypes)[number];

export const TransactionStatusTypeTranslated: Record<TransactionStatusType, string> = {
  pending: 'ממתין לתשלום',
  success: 'שולם',
  failed: 'נכשל',
  canceled: 'בוטל'
} as const;

/** payment provider that handled the transaction */
export const AllPaymentServiceTypes = ['tranzila', 'paypal'] as const;
export type PaymentServiceType = (typeof AllPaymentServiceTypes)[number];

export const PaymentServiceTypeTranslated: Record<PaymentServiceType, string> = {
  tranzila: 'טרנזילה',
  paypal: 'PayPal'
} as const;

export const AllCurrencyTypes = ['ils', 'usd', 'eur'] as const;
export type CurrencyType = (typeof AllCurrencyTypes)[number];

export const CurrencySymbol: Record<CurrencyType, string> = {
  ils: '₪',
  usd: '$',
  eur: '€'
} as const;

/** user object from Telegram Mini App initData */
export interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  photo_url?: string;
  allows_write_to_pm?: boolean;
}

/** billing (same as bb365 / bb-auth): who processes the payment */
export const AllPaymentProviders = ['tranzila', 'paypal'] as const;
export type PaymentProviderType = (typeof AllPaymentProviders)[number];

/** what the user picks at checkout; admins enable / disable each one (Admin > תשלומים).
 *  Telegram Stars are not offered: Telegram allows them for digital goods only. */
export const AllPaymentOptions = ['cc', 'bit', 'apay', 'gpay', 'paypal'] as const;
export type PaymentOptionType = (typeof AllPaymentOptions)[number];

export const PaymentOptionTranslated: Record<PaymentOptionType, string> = {
  cc: 'כרטיס אשראי',
  bit: 'ביט',
  apay: 'Apple Pay',
  gpay: 'Google Pay',
  paypal: 'PayPal'
};

/** the provider behind each option */
export const PaymentOptionProvider: Record<PaymentOptionType, PaymentProviderType> = {
  cc: 'tranzila',
  bit: 'tranzila',
  apay: 'tranzila',
  gpay: 'tranzila',
  paypal: 'paypal'
};

//#region orders
/**
 * pending_payment -> paid (payment verified) -> processing -> shipped (courier) / ready_for_pickup (pickup) -> completed.
 * canceled: by an admin, or an unpaid order that expired.
 */
export const AllOrderStatusTypes = ['pending_payment', 'paid', 'processing', 'shipped', 'ready_for_pickup', 'completed', 'canceled'] as const;
export type OrderStatusType = (typeof AllOrderStatusTypes)[number];

export const OrderStatusTranslated: Record<OrderStatusType, string> = {
  pending_payment: 'ממתין לתשלום',
  paid: 'שולם',
  processing: 'בטיפול',
  shipped: 'נשלח',
  ready_for_pickup: 'מוכן לאיסוף',
  completed: 'הושלם',
  canceled: 'בוטל'
};

/** courier = to the customer's door, post = Israel Post to the branch / locker near the customer's address, pickup = a pickup point */
export const AllDeliveryMethodTypes = ['courier', 'post', 'pickup'] as const;
export type DeliveryMethodType = (typeof AllDeliveryMethodTypes)[number];

export const DeliveryMethodTranslated: Record<DeliveryMethodType, string> = {
  courier: 'משלוח עד הבית',
  post: 'משלוח לסניף דואר',
  pickup: 'איסוף מנקודה'
};

/** courier and post need the customer's address (the post office picks the branch by it) */
export const deliveryNeedsAddress = (kind: DeliveryMethodType) => kind !== 'pickup';

/** "#1042" - the order number customers see (id + 1000) */
export const orderNumber = (id: number) => `#${id + 1000}`;
export const ORDER_NUMBER_OFFSET = 1000;
//#endregion

//#region money (agorot)
/** 14000 -> "₪140" / 4990 -> "₪49.90" */
export function formatMoney(agorot: number, currency: CurrencyType = 'ils'): string {
  const v = agorot / 100;
  const s = v.toLocaleString('he-IL', { minimumFractionDigits: Number.isInteger(v) ? 0 : 2, maximumFractionDigits: 2 });
  return `${CurrencySymbol[currency] ?? ''}${s}`;
}

/** "149.90" / "149,90" / 149.9 -> 14990 agorot; NaN for anything else */
export function parseMoney(value: unknown): number {
  const n =
    typeof value === 'number'
      ? value
      : Number.parseFloat(
          String(value ?? '')
            .replace(',', '.')
            .replace(/[^\d.-]/g, '')
        );
  return Number.isFinite(n) ? Math.round(n * 100) : Number.NaN;
}
//#endregion
